/**
 * Gestão de Chips/SIM — backend no geocoder (FastAPI), exposto em /geo/v1/chips.
 * Vocabulário alinhado ao device do Traccar: icc (ICCID), operadora (operator),
 * numero (phone). Auth via cookie de sessão (mesma origem → enviado automático).
 */

export type ChipStatus =
  | 'estoque'
  | 'bancada_teste'
  | 'enviado_tecnico'
  | 'ativo'
  | 'suspenso'
  | 'cancelado';

export interface Chip {
  id: number;
  tenant_id: number | null;
  icc: string;
  numero: string | null;
  operadora: string | null;
  fornecedor: string | null;
  plano_dados: string | null;
  status: ChipStatus;
  device_uniqueid: string | null;
  device_name: string | null;
  custo_mensal: number | null;
  ativado_em: string | null;
  cancelado_em: string | null;
  obs: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ChipInput {
  icc: string;
  numero?: string | null;
  operadora?: string | null;
  fornecedor?: string | null;
  plano_dados?: string | null;
  status?: ChipStatus;
  device_uniqueid?: string | null;
  device_name?: string | null;
  custo_mensal?: number | null;
  obs?: string | null;
}

export interface ChipStats {
  total: number;
  estoque: number;
  bancada_teste: number;
  enviado_tecnico: number;
  ativo: number;
  suspenso: number;
  cancelado: number;
  com_rastreador: number;
  com_embarcacao: number;
  sem_vinculo: number;
  custo_mensal_ativos: number;
}

export interface ChipImportResult {
  imported: number;
  updated: number;
  skipped: number;
  devices: number;
}

const BASE = '/geo/v1/chips';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  if (!r.ok) {
    let detail = `${r.status}`;
    try {
      const j = await r.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

export const STATUS_LABEL: Record<ChipStatus, string> = {
  estoque: 'Em estoque',
  bancada_teste: 'Bancada / teste',
  enviado_tecnico: 'Enviado ao técnico',
  ativo: 'Ativo',
  suspenso: 'Suspenso',
  cancelado: 'Cancelado',
};

export const chipsApi = {
  list: (params?: { status?: string; operadora?: string; q?: string; vinculado?: boolean }) => {
    const p = new URLSearchParams();
    if (params?.status) p.set('status', params.status);
    if (params?.operadora) p.set('operadora', params.operadora);
    if (params?.q) p.set('q', params.q);
    if (params?.vinculado !== undefined) p.set('vinculado', String(params.vinculado));
    const qs = p.toString();
    return req<Chip[]>(`${BASE}${qs ? `?${qs}` : ''}`);
  },
  stats: () => req<ChipStats>(`${BASE}/stats`),
  get: (id: number) => req<Chip>(`${BASE}/${id}`),
  create: (body: ChipInput) => req<Chip>(BASE, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<ChipInput>) =>
    req<Chip>(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => req<void>(`${BASE}/${id}`, { method: 'DELETE' }),
  link: (id: number, device_uniqueid: string, device_name?: string | null) =>
    req<Chip>(`${BASE}/${id}/link`, {
      method: 'POST',
      body: JSON.stringify({ device_uniqueid, device_name }),
    }),
  unlink: (id: number) => req<Chip>(`${BASE}/${id}/unlink`, { method: 'POST' }),
  importFromDevices: () => req<ChipImportResult>(`${BASE}/import-from-devices`, { method: 'POST' }),
};
